import React, { useContext, useEffect, useState } from "react";
import ButtonBar from "../../components/button-bar/ButtonBar";
import callApi from "../../components/call-api/call-api";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { Playlist as PlaylistType } from '../../types/Playlist';
import { Box, Button, Modal, Stack, Typography, useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowLeft, faArrowUp, faCancel, faMusic, faPenToSquare, faPlay, faRecordVinyl, faRemove, faTrash } from "@fortawesome/free-solid-svg-icons";
import { deletePlaylist, deletePlaylistItem, getPlaylist, postPlaylistItemShift } from "../../components/apis/music-api";
import { PlaylistItem } from "../../types/PlaylistItem";
import { format, getLetter, trackDuration, zeroPad } from "../../components/text-helper/text-helper";
import { LastFmPlaylistItem } from "../../types/LastFmPlaylist";
import { useMusicStore } from "../../components/store/store";
import { modalStyle } from "../../modals/modal-style";
import { PlayModes } from "../../types/PlayModes";
import { getPlaylistDuration } from "../../components/playlist-helper/playlist-helper";
import { PlaylistType as PlaylistEnum } from "../../types/PlaylistType";

type PlaylistProps = {
    id: string,
}

const Playlist = () => {
    const params = useParams<PlaylistProps>();
    const navigate = useNavigate();
    const { getMusicToken, getAccessToken } = useContext(AuthenticationContext);
    const { currentTrack, setTrackIndex, setPlaylist, setPlayMode } = useMusicStore();
    const [ playlistState, setPlaylistState ] = useState<PlaylistType>();
    const [ open, setOpen ] = useState(false);
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const apiUrl = process.env.REACT_APP_API_URL;
    
    useEffect(() => {
        callApi<PlaylistType>(getPlaylist(getAccessToken(), params.id ?? ''), setPlaylistState);
    }, [params, getAccessToken]);
    
    const deleteThis = async () => {
        if (playlistState !== undefined) {
            await deletePlaylist(getAccessToken(), playlistState.id ?? '');
            navigate(`/playlists`);
        }
    }

    const handleDelete = () => {
        deleteThis();
    }

    const createTrackList = () => {
        const musicToken = getMusicToken();
        return playlistState?.playlistItems?.map((t) => {
            const albumName = `${t.albumYear} - ${t.albumTitle}`;
            const trackName = `${zeroPad(t.track?.number ?? 0, 2)} - ${t.track?.name}`;
            return {
                letter: t.letter,
                artist: t.artistName,
                album: albumName,
                trackNumber: t.track?.number,
                name: t.track?.name,
                scrobbleArtist: format(t.artistName),
                src: `${apiUrl}/Stream/Artists/${(t.artistName)}/Albums/${albumName}/Tracks/${trackName}?accessToken=${musicToken}`
            } as LastFmPlaylistItem
        }) ?? [];
    }

    const createAlbumTrackList = () => {
        const musicToken = getMusicToken();
        return playlistState?.playlistItems?.map((a) => {
            const albumName = `${a.albumYear} - ${a.albumTitle}`;
            const tracks = a.tracks?.map((t) => {
                const trackName = `${zeroPad(t.number ?? 0, 2)} - ${t.name}`;
                return {
                    letter: a.letter,
                    artist: a.artistName,
                    album: albumName,
                    trackNumber: t.number,
                    name: t.name,
                    scrobbleArtist: format(a.artistName),
                    src: `${apiUrl}/Stream/Artists/${(a.artistName)}/Albums/${albumName}/Tracks/${trackName}?accessToken=${musicToken}`
                } as LastFmPlaylistItem;
            }) ?? [];

            return tracks;
        }).flat() ?? [];
    }

    const handlePlayClicked = () => {
        if (playlistState?.playlistItems !== undefined) {
            const trackList = playlistState.playlistType === 'Albums' ? createAlbumTrackList() : createTrackList();

            setPlayMode(playlistState.playlistType === 'Albums' ? PlayModes.AlbumPlaylist : PlayModes.TrackPlaylist);
            setPlaylist(trackList);
            setTimeout(() => setTrackIndex(1), 1000);
        }
    }

    const handleShift = async (item: PlaylistItem, direction: 'up' | 'down') => {
        await postPlaylistItemShift(getAccessToken(), playlistState?.id ?? '', item.number, direction);
        await callApi<PlaylistType>(getPlaylist(getAccessToken(), params.id ?? ''), setPlaylistState);
    }

    const handleRemove = async (item: PlaylistItem) => {
        await deletePlaylistItem(getAccessToken(), playlistState?.id ?? '', item.number ?? '0');
        await callApi<PlaylistType>(getPlaylist(getAccessToken(), params.id ?? ''), setPlaylistState);
    }

    const isPlaying = (playlistItem: PlaylistItem | undefined) => {
        return currentTrack?.trackNumber === playlistItem?.track?.number
            && currentTrack?.artist === playlistItem?.artistName
            && currentTrack?.album === `${playlistItem?.albumYear} - ${playlistItem?.albumTitle}`
            && currentTrack?.name === playlistItem?.track?.name;
    }

    const displayName = (item: PlaylistItem) => {
        const trackName = item.track?.name ?? item.albumTitle ?? format(item.artistName);
        const duration = item.track?.duration ?? item.duration ?? 0;
        return (
            <>
                {trackName} <Typography component={"span"} variant="body2" sx={{ color: '#999999' }}>({trackDuration(duration)})</Typography>
            </>
        );
    }

    const displaySubItems = (item: PlaylistItem) => {
        return item.track?.name ?
            <Typography component={"span"} variant="body2" sx={{ color: '#999999' }}>
                ({item.albumYear})&nbsp;
                <Link to={`/letters/${getLetter(item.artistName ?? '')}/artists/${item.artistName}/albums/${item.albumYear} - ${item.albumTitle}`}>
                    {item.albumTitle}
                </Link>
                {isSmall ? <><br />by&nbsp;</> : <>&nbsp;by&nbsp;</>}                
                <Link to={`/letters/${getLetter(item.artistName ?? '')}/artists/${item.artistName}`}>
                    {format(item.artistName ?? '')}
                </Link>
            </Typography> : item.albumTitle ?
            <Typography component="span" variant="body2" sx={{ color: '#999999' }}>
                ({item.albumYear})&nbsp;
                <Link to={`/letters/${getLetter(item.artistName ?? '')}/artists/${item.artistName}`}>
                    {format(item.artistName ?? '')}
                </Link>
            </Typography> : 
            <Typography component="span" variant="body2" sx={{ color: '#999999' }}>
                <Link to={`/letters/${getLetter(item.artistName ?? '')}/artists/${item.artistName}`}>
                    {format(item.artistName ?? '')}
                </Link>
            </Typography>
    }

    return (
        <>
            <Modal open={open} onClose={handleDelete}>
                <Box sx={modalStyle}>
                    <Stack spacing={1} direction="column">
                        <Box>
                            <Typography id="modal-modal-title" variant="h6" component="h2">
                                Delete {playlistState?.name}?
                            </Typography>
                        </Box>
                        <Box>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Are you sure that you want to delete this playlist and its artist relations?
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Button variant="contained" size="small" onClick={() => setOpen(false)} color="error">
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faCancel} className="icon" />
                                Cancel
                            </Button>
                            <Button variant="contained" size="small" onClick={handleDelete} color="warning">
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faTrash} className="icon" />
                                Delete
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Modal>
            <ButtonBar
                leftButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/Playlists`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon" />
                        Back To Playlists
                    </Button>,
                    <Button variant="contained" size="small" onClick={() => handlePlayClicked()}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faPlay} className="icon" />
                        Play
                    </Button>
                ]}
                rightButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/Playlists/${params.id}/edit`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon" />
                        Edit Playlist
                    </Button>,
                    <Button variant="contained" size="small" onClick={() => setOpen(true)}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faTrash} className="icon" />
                        Delete Playlist
                    </Button>
                ]} 
            />
            <Stack direction="column" spacing={2}>
                <Box>
                    <Typography variant='h5'>
                        <FontAwesomeIcon
                            color="primary" 
                            icon={playlistState?.playlistType === PlaylistEnum.Albums ? faRecordVinyl : faMusic}
                            className="icon"
                        />
                        {playlistState?.name ?? ''}
                    </Typography>
                    <Typography variant='body1' color="grey">
                        ({getPlaylistDuration(playlistState)})
                    </Typography>
                </Box>
                {playlistState?.playlistItems?.map((playlistItem: PlaylistItem, index: number) =>
                    <Box>
                        <Stack
                            direction={isSmall ? "column" : "row"}
                            spacing={1}
                            sx={{
                                borderTop: isSmall ? '1px solid #b3b3b3' : 'none',
                                paddingTop: isSmall ? '8px' : '0px'
                            }}
                        >
                            <Box>
                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ width: '32px', height: '24px', fontWeight: 'bold' }}>
                                        {(index < (playlistState?.playlistItems?.length ?? 0) - 1) && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                style={{ minWidth: 0 }}
                                                onClick={() => handleShift(playlistItem, 'up')}>
                                                <FontAwesomeIcon fontSize="large" color="primary" icon={faArrowDown} className="icon" style={{ marginRight: 0 }} />
                                            </Button>
                                        )}
                                    </Box>
                                    <Box sx={{ width: '32px', height: '24px', fontWeight: 'bold' }}>
                                        {(index > 0) && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                //disabled={isPlaying(track.number)}
                                                style={{ minWidth: 0 }}
                                                onClick={() => handleShift(playlistItem, 'down')}>
                                                <FontAwesomeIcon fontSize="large" color="primary" icon={faArrowUp} className="icon" style={{ marginRight: 0 }} />
                                            </Button>
                                        )}
                                    </Box>
                                    <Box sx={{ width: '32px', height: '24px', fontWeight: 'bold' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            style={{ minWidth: 0 }}
                                            onClick={() => handleRemove(playlistItem)}>
                                            <FontAwesomeIcon fontSize="large" color="primary" icon={faRemove} className="icon" style={{ marginRight: 0 }} />
                                        </Button>
                                    </Box>
                                    <Box sx={{ width: '32px', textAlign: 'center', color: 'green' }}>
                                        {isPlaying(playlistItem) && <FontAwesomeIcon fontSize="large" color="primary" icon={faMusic} className="icon" style={{ marginRight: 0 }} />}
                                    </Box>
                                </Stack>
                            </Box>
                            <Box>
                                {String(playlistItem.number).padStart(3, '0')}
                            </Box>
                            <Box>
                                <Stack direction="column" sx={{ marginTop: isSmall ? '-8px' : '0px' }}>
                                    <Box pr={2}>
                                        {displayName(playlistItem)}
                                    </Box>
                                    <Box>
                                        {displaySubItems(playlistItem)}
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                )}
            </Stack>
        </>
    )
}

export default Playlist;